/**
 * Unit Service
 * API integration para Unidades (posto de trabalho).
 */

import apiClient from './apiClient';
import {
  Unit,
  UnitNaLista,
  AlcanceDaUnidade,
  CreateUnitRequest,
  UpdateUnitRequest,
  UnitFilters,
} from '../types/unit';
import { PaginatedResponse } from '../types/common';

export interface UnitListParams {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: number;
  filters?: Partial<UnitFilters>;
}

class UnitService {
  private baseUrl = '/units';

  /**
   * Lista paginada de unidades
   */
  async getUnits(params: UnitListParams = {}): Promise<PaginatedResponse<UnitNaLista>> {
    const { page = 1, limit = 10, search, companyId, filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) queryParams.append('search', search);
    if (companyId) queryParams.append('companyId', companyId.toString());
    if (filters?.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters?.status) queryParams.append('status', filters.status);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams}`);
    const backendData = response.data;
    return {
      data: backendData.units ?? [],
      pagination: {
        total: backendData.total ?? 0,
        page,
        limit,
        totalPages: Math.ceil((backendData.total ?? 0) / limit),
      },
    } as PaginatedResponse<UnitNaLista>;
  }

  /**
   * Unidade por ID
   */
  async getUnitById(id: number | string): Promise<Unit> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Quantos setores, cargos e funcionários uma aplicação a esta unidade alcança (#541)
   */
  async getAlcance(id: number | string): Promise<AlcanceDaUnidade> {
    const { data } = await apiClient.get<AlcanceDaUnidade>(`${this.baseUrl}/${id}/alcance`);
    if (!data) throw new Error('Alcance da unidade não encontrado');
    return data;
  }

  /**
   * Unidades de uma empresa (para selects)
   */
  async getUnitsByCompanyId(companyId: number | string): Promise<Unit[]> {
    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}`);
    return response.data;
  }

  /**
   * Criar unidade
   */
  async createUnit(data: CreateUnitRequest): Promise<Unit> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Atualizar unidade
   */
  async updateUnit(id: number | string, data: UpdateUnitRequest): Promise<Unit> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Excluir unidade
   */
  async deleteUnit(id: number | string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

export const unitService = new UnitService();
export default unitService;
